/**
 * Trainer-session live room at `/sessions/:id/join`.
 *
 * Mounts a Daily.co iframe pointing at the room provisioned during
 * the Stripe webhook fulfillment. Both buyer + trainer use this
 * surface; the backend `/api/trainer-sessions/{id}/join` endpoint
 * returns a per-user meeting token (owner=true for the trainer).
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, VideoCameraIcon as VideoCamera, WarningIcon as Warning } from "@phosphor-icons/react";

export default function SessionJoin() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/trainer-sessions/${id}/join`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setErr(e?.response?.data?.detail || "Could not load session");
      });
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0E0E0E] text-white px-6 text-center">
        <div>
          <Warning size={36} className="mx-auto text-gold" />
          <p className="font-spectral text-2xl mt-3">Cannot join yet</p>
          <p className="text-sm text-white/70 mt-2 max-w-md">{err}</p>
          <Link to="/me/sessions" className="inline-flex items-center gap-1.5 mt-5 text-sm underline">
            <ArrowLeft size={14} /> Back to my sessions
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen grid place-items-center bg-black text-white text-sm">Joining session…</div>;
  }

  const url = data.room_url + (data.token ? `?t=${encodeURIComponent(data.token)}` : "");
  return (
    <div className="min-h-screen bg-black">
      <div className="h-12 bg-[#0E0E0E] text-white flex items-center justify-between px-4 border-b border-white/10">
        <Link to={data.is_trainer ? "/me/trainer-learning" : "/me/sessions"} className="inline-flex items-center gap-1.5 text-xs">
          <ArrowLeft size={12} /> Leave
        </Link>
        <p className="text-xs inline-flex items-center gap-1.5">
          <VideoCamera size={12} className="text-gold" /> Live training session
        </p>
        <span className="text-xs text-white/50">{data.is_trainer ? "Host" : "Guest"}</span>
      </div>
      <iframe
        title="Training session"
        src={url}
        allow="camera; microphone; fullscreen; speaker; display-capture; autoplay"
        className="w-full"
        style={{ height: "calc(100vh - 3rem)", border: 0 }}
        data-testid="session-iframe"
      />
    </div>
  );
}
